/**
 * quick-view-toggles.js
 *
 * Document-level delegated listeners for the quick-view modal section.
 * Two concerns:
 *
 *   1. OTP / Subscribe radio stack (#QvOtpOption / #QvSubOption) — mirrors
 *      sections/main-product-page.liquid:1980-2034 with Qv-prefixed IDs.
 *      The frequency <select> ships server-rendered with `disabled` so the
 *      initial OTP-active state is correct without a JS init pass; setMode
 *      flips the disabled flag on mode change.
 *
 *      Class names track the mockup (docs/mockups/quick-view-v2-split.html):
 *        Stack container : .qvs-subs   (was .xp-subs-stack / .qv-subs-stack)
 *        Stack item      : .qvs-sub    (was .xp-sub-item)
 *        Active state    : is-active   (was active)
 *
 *   2. Close button ([data-modal-close] inside the quick-buy-modal) —
 *      assets/modal.js:58 binds [data-modal-close] in connectedCallback(),
 *      which runs at modal mount time (before our section is injected).
 *      Our close button is therefore never bound by the component. We
 *      dispatch ThemeBus 'modal:close' directly.
 *
 * Why document-level: the Section Rendering API response is injected into
 * [data-modal-body] via innerHTML in assets/product-card.js. <script> tags
 * inserted via innerHTML do NOT execute, so any per-instance setup script
 * inside the section file would never run. Document-level delegation
 * registered once at page load survives every modal open/inject cycle.
 */
(function () {
  'use strict';

  var QV_MODAL_ID = 'quick-buy-modal';
  var ACTIVE = 'is-active';

  function $(sel, root) {
    return (root || document).querySelector(sel);
  }

  function setMode(mode) {
    var sub = $('#QvSubOption');
    var otp = $('#QvOtpOption');
    var hiddenInput = $('#QvSellingPlanInput');
    var freqSelect = $('#QvSellingPlanSelect');

    if (!sub && !otp) return; // Stack not rendered for this product

    if (mode === 'sub') {
      if (sub) {
        sub.classList.add(ACTIVE);
        sub.setAttribute('aria-checked', 'true');
      }
      if (otp) {
        otp.classList.remove(ACTIVE);
        otp.setAttribute('aria-checked', 'false');
      }
      if (freqSelect) freqSelect.disabled = false;
      if (hiddenInput) {
        var planId = (freqSelect && freqSelect.value) || (sub && sub.dataset.defaultPlan) || '';
        hiddenInput.value = planId;
      }
    } else {
      if (otp) {
        otp.classList.add(ACTIVE);
        otp.setAttribute('aria-checked', 'true');
      }
      if (sub) {
        sub.classList.remove(ACTIVE);
        sub.setAttribute('aria-checked', 'false');
      }
      // Disabled controls are excluded from form submission per HTML spec —
      // this prevents duplicate name="selling_plan" entries.
      if (freqSelect) freqSelect.disabled = true;
      if (hiddenInput) hiddenInput.value = '';
    }
  }

  // =========================================================
  //  Delivery panel (QvDeliveryPanel) — dual-row dates widget
  //  =========================================================
  //  Implements the mockup at docs/mockups/quick-view-v2.html:467-476.
  //  Two rows, single green pill:
  //    Row 1 (truck): Standard delivery — arrives [stdMin] – [stdMax]
  //    Row 2 (clock): Next day (£5.99) — order in [countdown] · arrives [ndArrival]
  //                                       (countdown drops post-cutoff)
  //
  //  Dispatch math mirrors the PDP's xp-dispatch-timer logic
  //  (sections/main-product-page.liquid:1963-2012):
  //    - 4pm Europe/London cutoff
  //    - Mon-Fri working days; weekends skipped
  //    - Pre-cutoff weekday: dispatch today
  //    - Post-cutoff or weekend: dispatch next working day
  //    - Standard delivery window: dispatch + 2 to + 4 working days
  //    - Next-day arrival: dispatch + 1 working day
  //
  //  Date arithmetic uses UTC `Date` objects whose component values
  //  represent London local time (extracted via Intl.DateTimeFormat).
  //  This stays correct for customers outside the UK — the dispatch
  //  promise is anchored to UK clock time, never the visitor's local
  //  timezone.

  var CUTOFF_HOUR = 16; // 4pm London — matches PDP xp-dispatch-timer
  var STD_MIN_DAYS = 2;
  var STD_MAX_DAYS = 4;

  function nowInLondon() {
    var fmt = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/London',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false
    });
    var parts = fmt.formatToParts(new Date());
    var get = function (t) {
      var p = parts.find(function (q) { return q.type === t; });
      return p ? parseInt(p.value, 10) : 0;
    };
    return new Date(Date.UTC(
      get('year'), get('month') - 1, get('day'),
      get('hour'), get('minute'), get('second')
    ));
  }

  function isWorkingDay(d) {
    var day = d.getUTCDay();
    return day !== 0 && day !== 6; // 0=Sun, 6=Sat
  }

  function addDaysUTC(d, n) {
    var c = new Date(d.getTime());
    c.setUTCDate(c.getUTCDate() + n);
    return c;
  }

  function nextWorkingDay(d) {
    var c = addDaysUTC(d, 1);
    while (!isWorkingDay(c)) c = addDaysUTC(c, 1);
    return c;
  }

  function addWorkingDays(d, n) {
    var c = new Date(d.getTime());
    while (n > 0) { c = nextWorkingDay(c); n--; }
    return c;
  }

  function fmtShort(d) {
    var DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    var MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return DAYS[d.getUTCDay()] + ' ' + d.getUTCDate() + ' ' + MONTHS[d.getUTCMonth()];
  }

  // Lightweight HTML-escape for date strings before innerHTML composition.
  // Dates come from our own fmtShort output so this is defensive only.
  function escHtml(s) {
    return String(s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  function updateDeliveryPanel() {
    var panel = document.getElementById('QvDeliveryPanel');
    if (!panel) return;
    var stdEl = panel.querySelector('[data-qvs-eta="std-range"]');
    var ndLineEl = panel.querySelector('[data-qvs-eta="nd-line"]');

    var now = nowInLondon();
    var beforeCutoff = isWorkingDay(now) && now.getUTCHours() < CUTOFF_HOUR;
    var dispatchDay = beforeCutoff ? now : nextWorkingDay(now);
    var ndArrival = nextWorkingDay(dispatchDay);
    var stdMin = addWorkingDays(dispatchDay, STD_MIN_DAYS);
    var stdMax = addWorkingDays(dispatchDay, STD_MAX_DAYS);

    if (stdEl) stdEl.textContent = fmtShort(stdMin) + ' – ' + fmtShort(stdMax);

    if (ndLineEl) {
      if (beforeCutoff) {
        var cutoffMs = Date.UTC(
          now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), CUTOFF_HOUR
        );
        var diff = cutoffMs - now.getTime();
        var timeStr;
        if (diff < 60000) {
          timeStr = 'under 1m';
        } else {
          var h = Math.floor(diff / 3600000);
          var m = Math.floor((diff % 3600000) / 60000);
          timeStr = (h > 0 ? h + 'h ' : '') + m + 'm';
        }
        ndLineEl.innerHTML =
          'order in <span class="qvs-eta-countdown">' + escHtml(timeStr) + '</span> &middot; arrives ' + escHtml(fmtShort(ndArrival));
      } else {
        // Post-cutoff or weekend: no countdown, just the arrival date
        ndLineEl.textContent = 'arrives ' + fmtShort(ndArrival);
      }
    }
  }

  var dispatchTimerId = null;
  function startDispatchTimer() {
    updateDeliveryPanel();
    if (dispatchTimerId) clearInterval(dispatchTimerId);
    dispatchTimerId = setInterval(updateDeliveryPanel, 60000);
  }
  function stopDispatchTimer() {
    if (dispatchTimerId) { clearInterval(dispatchTimerId); dispatchTimerId = null; }
  }

  // Defensive reset on modal open: handles re-open of the same product where
  // the previous interaction left DOM state mid-toggle (cache-hit case).
  // Fresh injects already ship with correct server-rendered state.
  //
  // Timing problem we have to work around: product-card.js fetches the
  // section markup via the Section Rendering API and innerHTML's it into
  // [data-modal-body] AFTER `modal:open` fires. The handler then dispatches
  // `EVENTS.quickBuyFormLoaded`, but that constant is undefined in
  // assets/event-constants.js — `new CustomEvent(undefined)` produces an
  // event name of literally "undefined" that nothing listens for.
  //
  // So we hook two things:
  //   1. modal:open / modal:close — for teardown and cache-hit cases where
  //      the body innerHTML may already be present
  //   2. A persistent MutationObserver on the modal-component subtree —
  //      fires every time #QvDispatchTimer appears (i.e. every successful
  //      Section Rendering API inject), letting us init the timer at the
  //      exact moment the element exists in the DOM.

  function initAfterMount() {
    if ($('#QvSubOption') || $('#QvOtpOption')) setMode('otp');
    if (document.getElementById('QvDeliveryPanel')) startDispatchTimer();
  }

  if (window.ThemeBus) {
    window.ThemeBus.addEventListener('modal:open', function (e) {
      if (e && e.detail && e.detail.id === QV_MODAL_ID) {
        // Cache-hit path: body may already be populated from a prior fetch
        setTimeout(initAfterMount, 0);
      }
    });
    window.ThemeBus.addEventListener('modal:close', function (e) {
      if (e && e.detail && e.detail.id === QV_MODAL_ID) {
        stopDispatchTimer();
      }
    });
  }

  // MutationObserver-based init: catches the async inject path even when
  // modal:open beats the fetch. We watch the whole quick-buy-modal subtree
  // and fire initAfterMount whenever the dispatch timer node appears.
  // Re-init whenever a NEW QvDeliveryPanel node appears — the first open AND every
  // product switch (the Section Rendering API replaces [data-modal-body] innerHTML,
  // producing a fresh panel node each time). Keying on node identity instead of a
  // `!dispatchTimerId` guard means switching products without a modal:close in
  // between still refreshes the ETA dates (otherwise the new panel kept its "…").
  var lastPanel = null;
  function setupModalObserver() {
    var modal = document.querySelector('modal-component[data-modal-id="' + QV_MODAL_ID + '"]');
    if (!modal || typeof MutationObserver === 'undefined') return;
    var obs = new MutationObserver(function () {
      var panel = document.getElementById('QvDeliveryPanel');
      if (panel && panel !== lastPanel) {
        lastPanel = panel;
        initAfterMount();
      }
    });
    obs.observe(modal, { childList: true, subtree: true });

    // modal.js stops click propagation at the dialog, so the document-level
    // click delegation never receives quick-view clicks. Bind the handler on
    // [data-modal-body] too — it lives INSIDE the dialog (so clicks reach it
    // before modal.js's stopPropagation) and the element persists across
    // product switches (only its innerHTML is replaced). This is what makes the
    // close button and the OTP/Sub toggle actually work.
    var body = modal.querySelector('[data-modal-body]');
    if (body && !body.__qvClickBound) {
      body.__qvClickBound = true;
      body.addEventListener('click', qvClickHandler);
    }
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupModalObserver);
  } else {
    setupModalObserver();
  }

  // ===== Click delegation =====
  // Bound below on BOTH document and the modal body. modal.js calls
  // stopPropagation() on the dialog (modal.js ~line 110), so clicks inside the
  // quick-view never bubble up to document — the [data-modal-body] binding in
  // setupModalObserver is what actually catches the close button and the
  // OTP/Sub toggle. (The document binding is a harmless fallback.)
  function qvClickHandler(e) {
    if (!e.target || !e.target.closest) return;

    // (a) Close button inside the quick-view modal.
    //     The section's .qvs-close is injected AFTER modal.js binds its handlers,
    //     so modal.js never wired our button up. Delegate to modal.js's OWN
    //     native [data-modal-close] button (which it binds to #handleClose at
    //     mount): that runs the full, correct close — hides the modal, unlocks
    //     body scroll, AND dispatches drawerOverlayHide so the shared blur
    //     backdrop's reference count is balanced and the overlay actually clears.
    //     A direct DOM close would orphan that overlay (count stuck > 0), leaving
    //     a blurred, click-blocked page. Fallback replicates #handleClose if the
    //     native button is somehow absent.
    var closeBtn = e.target.closest('[data-modal-close]');
    if (closeBtn && closeBtn.classList.contains('qvs-close')) {
      var modal = closeBtn.closest('modal-component[data-modal-id="' + QV_MODAL_ID + '"]');
      if (modal) {
        e.preventDefault();
        var nativeClose = modal.querySelector('[data-modal-close]:not(.qvs-close)');
        if (nativeClose) {
          nativeClose.click();
        } else {
          try { modal.isOpen = false; } catch (_) {}
          modal.classList.remove('is-modal-open');
          modal.classList.add('hidden');
          modal.setAttribute('aria-hidden', 'true');
          document.body.classList.remove('overflow-hidden');
          if (window.ThemeBus) {
            try {
              window.ThemeBus.dispatchEvent(new Event('drawer-overlay:hide'));
              window.ThemeBus.dispatchEvent(new Event('header-overlay:hide'));
            } catch (_) {}
          }
        }
        return;
      }
    }

    // (b) OTP/Sub stack click — guard against clicks inside the frequency
    //     selector so we don't toggle while a customer is choosing a plan.
    if (e.target.closest('.qvs-freq-select')) return;
    var stack = e.target.closest('.qvs-subs');
    if (!stack) return;
    var item = e.target.closest('.qvs-sub');
    if (!item) return;
    setMode(item.dataset.mode);
  }
  document.addEventListener('click', qvClickHandler);

  // ===== Keyboard support for radio stack =====
  document.addEventListener('keydown', function (e) {
    if (!e.target || !e.target.closest) return;
    // Don't hijack native <select> keyboard interaction (Space opens dropdown,
    // Enter confirms highlighted option). Mirrors the click-handler guard.
    if (e.target.closest('.qvs-freq-select')) return;
    var stack = e.target.closest('.qvs-subs');
    if (!stack) return;
    var item = e.target.closest('.qvs-sub');
    if (!item) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setMode(item.dataset.mode);
    }
  });

  // ===== Frequency change → keep hidden input in sync =====
  document.addEventListener('change', function (e) {
    if (!e.target || e.target.id !== 'QvSellingPlanSelect') return;
    var sub = $('#QvSubOption');
    if (!sub || !sub.classList.contains(ACTIVE)) return;
    var hiddenInput = $('#QvSellingPlanInput');
    if (hiddenInput) hiddenInput.value = e.target.value;
  });
})();
